import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import authService from '../../../api-authorization/AuthorizeService';
import toast from 'react-hot-toast';
import validator from 'validator';
import styles from './UserManagementContent.module.css';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';

const PAGE_SIZE = 15;

export default function UserManagement() {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedEmailToDelete, setSelectedEmailToDelete] = useState('');
  const [errorEmailMessage, setErrorEmailMessage] = useState('');
  const [errorRolesMessage, setErrorRolesMessage] = useState('');
  const [emailToInvite, setEmailToInvite] = useState('');
  const [roles, setRoles] = useState([]);
  const [update, setUpdate] = useState(0);
  const [changeFirstOption, setChangeFirstOption] = useState(false);

  // Admin state
  const isAdmin = useSelector((state) => state.app.isAdmin);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null); // 'invite' | 'lock'

  // Add state for tracking which user is being processed
  const [processingUserEmail, setProcessingUserEmail] = useState('');

  const [sortField, setSortField] = useState('email');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterEmail, setFilterEmail] = useState('');
  const [page, setPage] = useState(1);

  const [roleUpdateLoading, setRoleUpdateLoading] = useState({}); // { [email]: boolean }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/users/get-all-user-ids-and-emails`,
          {
            headers: !token ? {} : { Authorization: `Bearer ${token}` }
          }
        );
        const data = await response.json();
        setData(data);
        setLoading(false);
        setError(false);
      } catch (error) {
        setTimeout(() => {
          setError(true);
          setLoading(false);
        }, 1000);
      }
    };
    fetchUsers();
  }, [update]);

  // Fetch clients if admin
  useEffect(() => {
    if (!isAdmin) return;
    let ignore = false;
    const fetchClients = async () => {
      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/admin/clients/all`,
          {
            headers: !token ? {} : { Authorization: `Bearer ${token}` }
          }
        );
        const clientData = await response.json();
        if (!ignore) {
          setClients(clientData);
        }
      } catch (error) {
        // Optionally handle error
      }
    };
    fetchClients();
    return () => {
      ignore = true;
    };
  }, [isAdmin]);

  const handleDeleteUser = async () => {
    if (!selectedEmailToDelete) {
      return;
    } else {
      setLoading(true);
      const token = await authService.getAccessToken();
      const data = {
        clientId: selectedEmailToDelete
      };
      const requestOptions = {
        method: 'DELETE',
        headers: !token
          ? {}
          : {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
        body: JSON.stringify(data)
      };

      const fetchPromise = new Promise((resolve, reject) => {
        setTimeout(() => {
          fetch(
            `${process.env.REACT_APP_BASE_URL}/api/users/lock-out?email=${encodeURIComponent(selectedEmailToDelete)}`,
            requestOptions
          )
            .then((response) => {
              if (!response.ok) {
                reject('Lock out failed');
                setLoading(false);
                throw new Error('Lock out failed');
              }

              resolve('Lock out successful');
              setLoading(false);
              setUpdate(update + 1);
              setSelectedEmailToDelete('');
            })
            .catch((error) => {
              reject('Lock out failed');
              setLoading(false);
              console.error('Lock out failed:', error);
            });
        }, 1500);
      });

      toast.promise(fetchPromise, {
        loading: 'Loading...',
        success: 'Lock out successful',
        error: 'Lock out failed'
      });
    }
  };

  const inviteUser = async () => {
    if (!emailToInvite || roles.length === 0 || (isAdmin && !selectedClient)) {
      return;
    } else {
      setLoading(true);
      const data = isAdmin
        ? { email: emailToInvite, roles: roles, clientId: selectedClient.id }
        : { email: emailToInvite, roles: roles };

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      };

      const fetchPromise = new Promise((resolve, reject) => {
        setTimeout(() => {
          fetch(
            `${process.env.REACT_APP_BASE_URL}/api/users/invite-user`,
            requestOptions
          )
            .then((response) => {
              if (!response.ok) {
                reject(response.status);
                setLoading(false);
                throw new Error(response.status);
              }

              resolve('Invite successful');

              document.getElementById('manager').checked = false;
              document.getElementById('operator').checked = false;

              setLoading(false);
              setEmailToInvite('');
            })
            .catch((error) => {
              reject('Invite failed');
              setLoading(false);
            });
        }, 1500);
      });

      toast.promise(fetchPromise, {
        loading: 'Loading...',
        success: 'Invite successful',
        error: (error) => {
          return error === 500 ? 'User already exists!' : 'Invite failed';
        }
      });
    }
  };

  const handleRoleChange = (role) => (e) => {
    if (e.target.checked) {
      setRoles([...roles, role]);
    } else {
      setRoles(roles.filter((r) => r !== role));
    }
  };

  // Invite button handler (admin: show dialog, non-admin: direct)
  const handleInviteUser = () => {
    if (!validator.isEmail(emailToInvite)) {
      return setErrorEmailMessage('Please enter a valid email address.');
    } else if (roles.length === 0) {
      setErrorEmailMessage('');
      return setErrorRolesMessage('Please select at least one role.');
    }
    if (isAdmin && !selectedClient) {
      setErrorRolesMessage('Please select a client.');
      return;
    }
    setErrorRolesMessage('');
    if (isAdmin) {
      setDialogType('invite');
      setDialogOpen(true);
    } else {
      inviteUser();
    }
  };

  // Lock button handler: show dialog instead of confirm()
  const handleLockUser = () => {
    if (!selectedEmailToDelete) return;
    setDialogType('lock');
    setDialogOpen(true);
  };

  // Helper to get the selected user object
  const selectedUser = useMemo(() => {
    if (!selectedEmailToDelete || !data) return null;
    return data.find(u => u.email === selectedEmailToDelete);
  }, [selectedEmailToDelete, data]);

  // Dialog message
  const getDialogMessage = () => {
    if (dialogType === 'invite' && isAdmin && selectedClient) {
      return (
        <>
          Are you sure you want to invite <strong>{emailToInvite}</strong> to client <strong>{selectedClient.name}</strong>?
        </>
      );
    }
    if (dialogType === 'lock' && selectedEmailToDelete) {
      if (selectedUser?.isLocked) {
        return (
          <>
            Are you sure you want to <strong>unlock</strong> user <strong>{selectedEmailToDelete}</strong>?
          </>
        );
      } else {
        return (
          <>
            Are you sure you want to <strong>lock out</strong> user <strong>{selectedEmailToDelete}</strong>?
          </>
        );
      }
    }
    return '';
  };

  // Dialog confirm
  const handleDialogConfirm = () => {
    setDialogOpen(false);
    if (dialogType === 'invite') {
      inviteUser();
    } else if (dialogType === 'lock') {
      // Pass correct unlock/lock intent
      handleLockToggle(selectedEmailToDelete, selectedUser?.isLocked);
    }
  };

  // Dialog cancel
  const handleDialogCancel = () => {
    setDialogOpen(false);
  };

  // Lock/Unlock handler
  const handleLockToggle = async (email, shouldUnlock) => {
    setProcessingUserEmail(email);
    const token = await authService.getAccessToken();
    const endpoint = shouldUnlock
      ? `${process.env.REACT_APP_BASE_URL}/api/users/unlock-out?email=${encodeURIComponent(email)}`
      : `${process.env.REACT_APP_BASE_URL}/api/users/lock-out?email=${encodeURIComponent(email)}`;
    const requestOptions = {
      method: 'POST',
      headers: !token
        ? {}
        : {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
    };

    const fetchPromise = new Promise((resolve, reject) => {
      fetch(endpoint, requestOptions)
        .then((response) => {
          if (!response.ok) {
            reject(shouldUnlock ? 'Unlock failed' : 'Lock out failed');
            setProcessingUserEmail('');
            throw new Error(shouldUnlock ? 'Unlock failed' : 'Lock out failed');
          }
          resolve(shouldUnlock ? 'Unlock successful' : 'Lock out successful');
          setProcessingUserEmail('');
          setUpdate((u) => u + 1);
        })
        .catch((error) => {
          reject(shouldUnlock ? 'Unlock failed' : 'Lock out failed');
          setProcessingUserEmail('');
          console.error(error);
        });
    });

    toast.promise(fetchPromise, {
      loading: shouldUnlock ? 'Unlocking...' : 'Locking...',
      success: shouldUnlock ? 'Unlock successful' : 'Lock out successful',
      error: shouldUnlock ? 'Unlock failed' : 'Lock out failed'
    });
  };

  // Role update handler
  const handleRoleChangeCheckbox = async (email, role, checked) => {
    // Find the user in the current data
    const user = data.find(u => u.email === email);
    if (!user) return;

    // Build new roles list
    let newRoles = [];
    if (role === 'Manager') {
      newRoles = [
        ...(checked ? ['Manager'] : []),
        ...(user.isOperator ? ['Operator'] : [])
      ];
    } else if (role === 'Operator') {
      newRoles = [
        ...(user.isManager ? ['Manager'] : []),
        ...(checked ? ['Operator'] : [])
      ];
    }

    setRoleUpdateLoading(prev => ({ ...prev, [email]: true }));

    try {
      const token = await authService.getAccessToken();
      const requestOptions = {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ Email: email, Roles: newRoles })
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/users/update-roles`,
        requestOptions
      );
      if (!response.ok) throw new Error('Failed to update roles');

      // Update local state for immediate feedback
      setData(prev =>
        prev.map(u =>
          u.email === email
            ? {
                ...u,
                isManager: newRoles.includes('Manager'),
                isOperator: newRoles.includes('Operator')
              }
            : u
        )
      );
      toast.success('Roles updated');
    } catch (err) {
      toast.error('Failed to update roles');
    } finally {
      setRoleUpdateLoading(prev => ({ ...prev, [email]: false }));
    }
  };

  // Filtered and sorted user list
  const filteredSortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    let filtered = data;
    if (filterEmail.trim()) {
      const filter = filterEmail.trim().toLowerCase();
      filtered = filtered.filter(user =>
        user.email && user.email.toLowerCase().includes(filter)
      );
    }
    const sorted = [...filtered].sort((a, b) => {
      let valA, valB;
      if (sortField === 'email') {
        valA = a.email?.toLowerCase() ?? '';
        valB = b.email?.toLowerCase() ?? '';
      } else if (sortField === 'isLocked') {
        valA = a.isLocked ? 1 : 0;
        valB = b.isLocked ? 1 : 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [data, filterEmail, sortField, sortDirection]);

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(filteredSortedData.length / PAGE_SIZE));
  const pagedData = filteredSortedData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to first page when filter or sort changes
  useEffect(() => {
    setPage(1);
  }, [filterEmail, sortField, sortDirection]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <>
      {/* Unified Confirmation Dialog for Invite and Lock */}
      <Dialog open={dialogOpen} onClose={handleDialogCancel}>
        <DialogTitle>
          {dialogType === 'invite' ? 'Confirm Invite' : dialogType === 'lock' ? 'Confirm Lock Out' : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getDialogMessage()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDialogConfirm} color="primary" variant="outlined">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <div className="w-full">
        <div className={styles.container}>
          <div className="space-y-12 w-full">
            <div className="border-b border-gray-900/10 pb-4 w-full">
              <h2 className="font-semibold leading-7 text-gray-900 text-xl pb-4">
                Invite New User
              </h2>

              <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Email:
                  </label>
                  <div className="mt-2">
                    <input
                      onChange={(e) => setEmailToInvite(e.target.value)}
                      value={emailToInvite}
                      type="email"
                      id="UserEmail"
                      placeholder="user@agtechlogic.com"
                      className="block overflow-hidden rounded-md border border-gray-200 px-3 py-2 shadow-sm focus-within:border-orange-600 focus-within:ring-1 focus-within:ring-orange-600 mt-1 w-full p-0 sm:text-sm placeholder:text-gray-300"
                    />

                    {errorEmailMessage && (
                      <p className="text-xs text-red-500">
                        {errorEmailMessage}
                      </p>
                    )}
                  </div>
                </div>

                {/* Admin: Client dropdown */}
                {isAdmin && clients.length > 0 && (
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="client"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Client:
                    </label>
                    <div className="mt-2">
                      <Autocomplete
                        disablePortal
                        options={clients}
                        getOptionLabel={(option) => option.name}
                        value={selectedClient}
                        onChange={(e, newValue) => setSelectedClient(newValue)}
                        sx={{ width: '100%', background: "white", borderRadius: 1 }}
                        size="small"
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </div>
                  </div>
                )}

                <div className="col-span-full">
                  <fieldset>
                    <legend className="text-sm font-semibold leading-6 text-gray-900">
                      Roles:
                    </legend>
                    <div className="mt-6 space-y-6">
                      <div className="flex items-center gap-x-3">
                        <input
                          onChange={handleRoleChange('Manager')}
                          id="manager"
                          name="manager"
                          type="checkbox"
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <div className="text-sm leading-6">
                          <label
                            htmlFor="manager"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Manager
                          </label>
                          <p className="text-gray-500">Managerial access</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-x-3">
                        <input
                          onChange={handleRoleChange('Operator')}
                          id="operator"
                          name="operator"
                          type="checkbox"
                          className="h-4 w-4 border-gray-300   text-indigo-600 focus:ring-indigo-600"
                        />
                        <div className="text-sm leading-6">
                          <label
                            htmlFor="operator"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Operator
                          </label>
                          <p className="text-gray-500">Employee access</p>
                        </div>
                      </div>
                    </div>

                    {errorRolesMessage && (
                      <p className="text-xs text-red-500">
                        {errorRolesMessage}
                      </p>
                    )}
                  </fieldset>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-end gap-x-6">
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  onClick={() => setEmailToInvite('')}
                  sx={{ mr: 2 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="outlined"
                  color="primary"
                  onClick={handleInviteUser}
                  disabled={
                    loading ||
                    !emailToInvite ||
                    roles.length === 0 ||
                    (isAdmin && !selectedClient)
                  }
                >
                  Invite
                </Button>
              </div>
            </div>
            
            <div className="border-b border-gray-900/10 pb-4 w-full">
              <h2 className="font-semibold leading-7 text-gray-900 text-xl pb-4 pt-8">
                User Access Management
              </h2>
              <div className="mt-8" style={{ maxWidth: 700 }}>
                <div style={{ marginBottom: 16, maxWidth: 350 }}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={filterEmail}
                    onChange={e => setFilterEmail(e.target.value)}
                    placeholder="Filter by email..."
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-label': 'Filter by email' }}
                    className="block overflow-hidden rounded-md border border-gray-200 px-3 py-2 shadow-sm focus-within:border-orange-600 focus-within:ring-1 focus-within:ring-orange-600 mt-1 w-full p-0 sm:text-sm placeholder:text-gray-300"
                  />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-300" style={{ width: '100%' }}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-r border-gray-300 cursor-pointer select-none"
                          style={{ width: '40%' }}
                          onClick={() => handleSort('email')}
                        >
                          Email
                          <span style={{ marginLeft: 6 }}>
                            {sortField === 'email' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                          </span>
                        </th>
                        <th
                          className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-r border-gray-300"
                          style={{ width: '30%' }}
                        >
                          Role
                        </th>
                        <th
                          className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer select-none"
                          style={{ width: '30%' }}
                          onClick={() => handleSort('isLocked')}
                        >
                          Lock / Unlock
                          <span style={{ marginLeft: 6 }}>
                            {sortField === 'isLocked' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pagedData.length > 0 ? (
                        pagedData.map((user) => (
                          <tr key={user.email}>
                            <td className="px-4 py-2 text-sm text-gray-900 border-r border-gray-300">{user.email}</td>
                            <td className="px-4 py-2 border-r border-gray-300">
                              <label style={{ marginRight: 16, display: 'inline-flex', alignItems: 'center' }}>
                                <Checkbox
                                  checked={!!user.isOperator}
                                  disabled={roleUpdateLoading[user.email]}
                                  onChange={e =>
                                    handleRoleChangeCheckbox(user.email, 'Operator', e.target.checked)
                                  }
                                  color="primary"
                                  size="small"
                                  sx={{
                                    p: 0.5,
                                    mr: 1,
                                    '& .MuiSvgIcon-root': { fontSize: 20 }
                                  }}
                                />
                                <span className="text-sm text-gray-900">Operator</span>
                              </label>
                              <label style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Checkbox
                                  checked={!!user.isManager}
                                  disabled={roleUpdateLoading[user.email]}
                                  onChange={e =>
                                    handleRoleChangeCheckbox(user.email, 'Manager', e.target.checked)
                                  }
                                  color="primary"
                                  size="small"
                                  sx={{
                                    p: 0.5,
                                    mr: 1,
                                    '& .MuiSvgIcon-root': { fontSize: 20 }
                                  }}
                                />
                                <span className="text-sm text-gray-900">Manager</span>
                              </label>
                            </td>
                            <td className="px-4 py-2" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <Button
                                variant="outlined"
                                color={user.isLocked ? "success" : "primary"}
                                size="small"
                                disabled={processingUserEmail === user.email}
                                onClick={() => {
                                  setSelectedEmailToDelete(user.email);
                                  setDialogType('lock');
                                  setDialogOpen(true);
                                }}
                              >
                                {user.isLocked ? "Unlock" : "Lock"}
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-4 py-2 text-center text-gray-500">
                            {loading ? "Loading users..." : "No users found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination controls */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 16 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                    style={{ marginRight: 8 }}
                  >
                    Previous
                  </Button>
                  <span style={{ fontSize: '0.95em', marginRight: 8 }}>
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}